import * as React from "react";
import { ApprenantApi, CopReassignmentDto, ELevelName } from "../../../../api";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import * as Yup from "yup";
import styled from "styled-components";
import { Classes, Dialog } from "@blueprintjs/core";
import { CustomBulletList, FGWalterSelectInput, SmallFormGenerator } from "../../../../components";
import { ETLCodes } from "../../../../locales";
import { FGCustomPanel, FieldGroup } from "nsitools-react";
import { useReferential } from "../../../../hooks/useReferential";

const StyledDialog = styled(Dialog)`
  width: 700px;
  height: auto;
  background-color: white;
`;

export interface ICopReassignmentDialogProps {
  idsApprenant: number[];
  dialogOpen: boolean;
  onClose: () => void;
}

export const CopReassignmentDialog: React.FunctionComponent<ICopReassignmentDialogProps> = ({
  idsApprenant = [],
  dialogOpen,
  onClose
}) => {
  const { t } = useTl();
  const api = useApiService(ApprenantApi);

  const { data, loading, saveItem, saving, validationErrors } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => {
          return api.apprenantGetCopReassignment({ request_body: idsApprenant });
        },
        saveApiFn: async (d: CopReassignmentDto) => {
          return api.apprenantSaveCopReassignment({ CopReassignmentDto: d });
        },
        onSaved: () => onClose()
      }),
      [api, idsApprenant, onClose]
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape({
        newIdCop: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
      }),
    [t]
  );

  const [usersCop, usersCopLoading] = useReferential(a =>
    a.referentialGetUsersByRoles({ ELevelName: [ELevelName.COP], currentIduser: 0 })
  );

  return (
    <StyledDialog title={t(ETLCodes.ReassignationCop)} isOpen={dialogOpen} onClose={() => onClose()}>
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          validationSchema={FormSchema}
          onCancel={() => onClose()}
          showDeleteButton={false}
          saving={saving}
          loading={loading}
          validationErrors={validationErrors}
        >
          <FieldGroup>
            <FGCustomPanel>
              {ctx => (
                <CustomBulletList
                  title={t(ETLCodes.COPsARemplacer)}
                  loading={loading}
                  items={
                    ctx?.formik?.values?.cops?.length > 0
                      ? ctx?.formik?.values?.cops?.map(c => ({ text: c }))
                      : [{ text: t(ETLCodes.Aucun) }]
                  }
                />
              )}
            </FGCustomPanel>
            <FGWalterSelectInput
              name="newIdCop"
              label={t(ETLCodes.CopRemplacant)}
              items={usersCop}
              loading={usersCopLoading}
            />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </StyledDialog>
  );
};
